let store = require("../store");
let actions = require("../actions");

let RecordingCalendar = require("../widget/RecordingCalendar");
let RecordingChart = require("../widget/RecordingChart");
let RecordingTape = require("../widget/RecordingTape");
let RecordingUtil = require("../util/RecordingUtil");
let SubjectData = require("../widget/SubjectData");
let Stepper = require("../widget/Stepper");

let {
  Tabs,
  Drop,
  IconButton,
  Pulse,
  Calendar
} = mc.widgets;

class RecordingView extends React.Component {
  constructor(props) {
    super(props);

    let { studyId, subjectId } = props;

    let state = store.getState();
    let subject = state.subjects[subjectId];
    let study = state.study[studyId];
    let annotations = state.annotations[subjectId] || [];
    let prefs = state.prefs;

    this.state = {
      subject,
      study,
      annotations,
      prefs,
      channels: {},
      cached: null
    };
  }

  componentDidMount() {
    let { studyId, subjectId, startTs } = this.props;
    let { prefs } = this.state;

    this.unsubscribe = store.subscribe(this.handleStoreChange.bind(this));

    actions.loadSubject(studyId, subjectId);
    actions.loadAnnotations(studyId, subjectId);
    actions.loadStudy(studyId);

    this.loadRawData(+startTs, prefs.deviceDataScrollPeriod);
  }

  componentWillUnmount() {
    this.unsubscribe();
  }

  componentDidUpdate(prevProps, prevState) {
    let { startTs } = this.props;
    let { prefs } = this.state;

    if (prevProps.startTs !== startTs) {
      this.loadRawData(+startTs, prefs.deviceDataScrollPeriod);
    }
  }

  handleStoreChange() {
    let { studyId, subjectId } = this.props;

    let state = store.getState();
    let subject = state.subjects[subjectId];
    let study = state.study[studyId];
    let annotations = state.annotations[subjectId] || [];
    let prefs = state.prefs;

    this.setState({
      subject,
      study,
      annotations,
      prefs
    });
  }

  loadRawData(startTs, period) {
    let { studyId, subjectId } = this.props;
    let stopTs = startTs + this.getPeriod(period);
    let url = `/studies/${studyId}/subjects/${subjectId}/data?from=${startTs}&to=${stopTs}`;

    // Cache for incremental channel accumulation
    let channels = new Map();
    // State update once all pages are fetched
    let handleDone = function() {
        let cached = Array.from(channels.values());
        let _channels = { ...this.state.channels, [startTs]: cached };
        this.setState({ channels: _channels, cached });
    };
    // Nested incremental request handler that follows links->next->href pointers if present
    let processPage = function (rawResponse) {
        let response = mc.ajax.parse(rawResponse);
        if (response.channels) {
            response.channels.forEach((ch) => {
                let rkey = JSON.stringify([ch.recording.id, ch.type]);
                if (channels.has(rkey)) {
                    // merge with previous page's channels
                    channels.get(rkey).times.push(...ch.times);
                    var ckeys = new Set([
                        ...Object.keys(ch.values),
                        ...Object.keys(channels.get(rkey).values)]);
                    for (let k of ckeys.values()) {
                            if (k in channels.get(rkey).values && k in ch.values) {
                                // already seen channel type -> extend
                                channels.get(rkey).values[k].push(...ch.values[k]);
                            }
                            else if (k in ch.values) {
                                // new channel type -> create
                                channels.get(rkey).values[k] = ch.values[k];
                            }
                    }
                }
                else {
                    // set first channel page as a whole
                    channels.set(rkey, ch);
                }
            });
        }

        // Iterate if needed
        if (response.links && response.links.length && response.links[0].rel === "next") {
            // Next page defined
            //  - recurse to fetch it
            //  - return promise for proper nesting (so catch works)
            return mc.ajax.get(response.links[0].href.replace("v1/", "")).then(processPage.bind(this));
        }
        // Render on last page
        (handleDone.bind(this))();
    };

    // Ensure loading spinner is up in-between pagination requests
    //   - setTimeout to avoid stateless Spinner issue: _spinner is null @spin mc-ui.js:47
    //   - +1 spin prevents some race conditions with Spinner set-to-0 timeout vs chained ajax calls
    setTimeout(function() {
        mc.ui.spin(1);
    });

    // Kickstart first (potentially single) page fetching
    mc.ajax.get(url).then(processPage.bind(this))
        .catch(function (error) {
      mc.logger.error(error.toString());
    }).finally(function() {
        // End aggregate loading
        mc.ui.spin(-1);
    });
  }

  // ensure backwards compatibility if options change
  getExtent(extent) {
    let values = RecordingView.EXTENT_OPTIONS.map((opt) => opt.value);

    if (values.includes(extent)) {
      return extent;
    }

    return 43200000;
  }

  // ensure backwards compatibility if options change
  getPeriod(period) {
    let values = RecordingView.PERIOD_OPTIONS.map((opt) => opt.value);

    if (values.includes(period)) {
      return period;
    }

    return 30000;
  }

  setStartTime(startTs) {
    mc.router.refresh({ startTs });
  }

  setDate(dayStartMsUtc, refTs) {
    this.setStartTime(refTs);
  }

  setPeriod(value, changed) {
    if (changed) {
      actions.setPref("deviceDataScrollPeriod", value);

      this.loadRawData(+this.props.startTs, value);
    }
  }

  setExtent(value, changed) {
    if (changed) {
      actions.setPref("deviceDataScrollExtent", value);
    }
  }

  toggleAutoScale() {
    let autoScale = !this.state.prefs.deviceDataAutoScale;

    actions.setPref("deviceDataAutoScale", autoScale);
  }

  openAnnotations() {
    let { subject, study, annotations } = this.state;

    mc.ui.modal(<SubjectData subject={subject} annotations={annotations} activities={study.activities || []} isModal={true}/>);
  }

  saveAnnotation(annotation, method) {
    let { studyId, subjectId } = this.props;

    actions.saveAnnotation(annotation, method).then(function (payload) {
      actions.loadAnnotations(studyId, subjectId);
    });
  }

  render() {
    let {
      subject,
      study,
      prefs,
      annotations,
      channels,
      cached
    } = this.state;

    let {
      studyId,
      subjectId,
      startTs
    } = this.props;

    startTs = parseInt(startTs);

    if (!subject) {
      return null;
    }

    let user = store.getState().user.user;
    let recordings = subject.recordings;
    let timezone = subject.timezone;
    let period = this.getPeriod(prefs.deviceDataScrollPeriod);
    let extent = this.getExtent(prefs.deviceDataScrollExtent);
    let autoScale = prefs.deviceDataAutoScale || false;
    let annos = annotations.filter(function (a) {
      return a.event && !a.event.relationship;
    });

    let charts = (channels[startTs] || cached || []).map((function (channel) {
      let device = subject.devices.find(function (d) {
        return d.udid === channel.recording.udid;
      });

      return (
        <RecordingChart
          key={[channel.type, channel.recording.id]}
          channel={channel}
          annotations={annos}
          startTs={startTs}
          period={period}
          autoScale={autoScale}
          studyId={studyId}
          subjectId={subjectId}
          timezone={timezone}
          authorId={user.id}
          deviceSerial={(device || {}).displayName}
          disabled={(study || {}).lifecycleState === "COMPLETE"}
          saveAnnotation={this.saveAnnotation.bind(this)}/>
      );
    }).bind(this));

    let sessions = RecordingUtil.sessions(recordings);

    let bounds = sessions.find(function (session) {
      return startTs >= session[0] && startTs <= session[1];
    });

    let pulse = (
      <Pulse margin={50}/>
    );

    let message = (
      <p className="rc-rec-view-message">
        {tx("no_data")}
      </p>
    );

    let content = charts && charts.length ? charts : message;

    let href = mc.router.resolve("SUBJECT_DETAIL_VIEW", this.props);

    let onEscape = null;

    let referrer = mc.router.referrer();

    if (referrer && referrer.id === "METRIC_DETAIL_VIEW") {
      onEscape = () => location.hash = referrer.hash;
    }

    return (
      <div className="rc-rec-view" data-hidden={subject.isHidden}>
        <div className="mc-split-layout">
          <h1>
            <a href={href}>
              {subject.displayName}
            </a>
            <span>{" › " + mc.format.datetime(startTs, timezone)}</span>
          </h1>
          <RecordingCalendar
            recordings={recordings}
            ts={startTs}
            tz={timezone}
            onSelect={(dayStartMsUtc, recStartTs) => this.setStartTime(recStartTs)}/>
        </div>
        <div className="rc-rec-view-reel">
          <Tabs
            options={RecordingView.EXTENT_OPTIONS}
            role="radiogroup"
            value={extent}
            onSelect={this.setExtent.bind(this)}/>
          <RecordingTape
            startTs={startTs}
            timezone={timezone}
            pad={extent / 2}
            sessions={sessions}
            annotations={annos}
            onSetStartTime={this.setStartTime.bind(this)}/>
        </div>

        <div className="rc-rec-view-stepper">
          <Stepper
            ts={startTs}
            bounds={bounds || [+Infinity, -Infinity]}
            periods={RecordingView.PERIOD_OPTIONS}
            period={period}
            autoScale={autoScale}
            onSetTime={this.setStartTime.bind(this)}
            onSetPeriod={this.setPeriod.bind(this)}
            onScale={this.toggleAutoScale.bind(this)}
            onSearch={this.openAnnotations.bind(this)}
            onEscape={onEscape}/>
        </div>

        <div className="rc-rec-view-charts">
          { Object.keys(channels).length ? content : pulse }
        </div>
      </div>
    );
  }
}

RecordingView.EXTENT_OPTIONS = [
  {
    value: 300000,
    label: "5m"
  },
  {
    value: 3600000,
    label: "1h"
  },
  {
    value: 43200000,
    label: "12h"
  }
];

RecordingView.PERIOD_OPTIONS = [
  {
    value: 6000,
    label: "6s"
  },
  {
    value: 15000,
    label: "15s"
  },
  {
    value: 30000,
    label: "30s"
  },
  {
    value: 60000,
    label: "60s"
  }
];

module.exports = RecordingView;